---
title: "Smart, boolean, and deep search"
description: "Chosen has three search modes — AI deep search (the default), smart, and boolean. A side-by-side comparison and clear guidance on which one to reach for."
category: "search"
keywords:
  - "smart search vs deep search"
  - "AI candidate search"
  - "which search mode"
  - "ATS search modes"
  - "natural language candidate search"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
  parent: "/guides/search"
---

**AI deep search** is the default: describe who you want and an AI pipeline reads resumes and ranks fit. Flip to the standard bar for **smart search** — instant, plain English, free — when the query is simple, or for **boolean** when natural language is being too loose and you want exact operators. Chosen remembers the last mode you used.

All three modes use the same query bar. The bar has a mode toggle: a lightning-bolt icon for AI search (selected by default) and a magnifying-glass icon for standard search. Boolean isn't a separate button — you get it by typing boolean operators into the standard bar. Clearing an AI search puts you back on the full table without changing the mode.

## The three modes

| | Smart search | Boolean search | Deep search |
| --- | --- | --- | --- |
| **Speed** | Instant | Instant | ~30 seconds to a few minutes |
| **What it searches** | Parsed fields, skills, and free text on candidates already in Chosen | The same, matched literally | The same, plus resume body, with an LLM rating each candidate |
| **How you write it** | A plain sentence | `AND` / `OR` / `NOT`, quotes, parentheses | A plain sentence |
| **When it's right** | "Engineers in NYC" | "senior engineer" must mean the phrase, not two words | "Engineers who worked on payments infra at a Series B" |
| **Cost** | Free | Free | Counts against an expensive usage tier |

## Smart search — instant

Type the thing you would say to a recruiter sitting next to you. An interpreter maps your sentence to candidate field filters plus a meaning-based match, then runs it against the people in your system. It is instant, it is free, and it is right most of the time — which makes it the right tool for simple queries even though AI search is the default.

Good queries for smart search:

- `senior backend engineers in NYC, ideally with payments experience`
- `designers who have led 0-to-1 product work at startups`
- `data scientists with healthcare domain experience`

Be specific about what matters. "Engineer" is too vague to be useful; "engineer with iOS experience" gives you a real result set.

Typing a person's name works too. Anyone whose name matches comes first — first name, last name, any capitalization, "jordan" finds "JORDAN LEE" — ahead of whatever else the sentence matched.

## Boolean search — when natural language is too generous

Sometimes smart search is too eager. It matches "senior" and "engineer" separately when you needed the exact phrase, or it pads the results with near-misses. Boolean mode hands you the controls: uppercase `AND`, `OR`, `NOT`, double quotes for exact phrases, parentheses to group.

```
"senior engineer" AND payments AND (NYC OR remote)
```

That is precise in a way a sentence can't be. The [boolean syntax page](/guides/search/boolean-syntax) is the full cheat-sheet.

## Deep search — the default

Deep search is a multi-stage AI pipeline. It plans your query, pulls a candidate set, has an LLM read each resume and score fit, and builds result columns specific to what you asked. It also adds columns smart search can't — for "people who worked at FANG," a column listing which FANG companies appear on each resume.

It is slower and it costs against a usage tier. It is the default because it understands the messy, real-world queries; when you already know exactly what you want (a name, a city, an exact phrase), flip to standard — instant and free. The [deep search run page](/guides/search/deep-search-runs) walks through what happens during one.

<Callout type="tip" title="If smart search returned who you wanted, stop">
The point of deep search is the hard queries. If the standard search already surfaced the right people, running a deep search on the same query mostly spends your quota to re-confirm what you already had.
</Callout>

## When it gets it wrong

- **Smart search pads results.** If a query returns near-misses, tighten the wording or switch to boolean for exact phrases.
- **Boolean returns nothing.** Usually too many `AND` clauses stacked together — see [why a search returns no results](/guides/search/why-no-results).
- **Deep search still misses someone.** A candidate has to make the recall set before the AI scores them. If your pool is large, narrowing the query helps more than re-running it.

## Limits

None of these search the open web. They run against candidates already imported into Chosen. Finding new people is a separate flow — [sourcing](/guides/sourcing). Search also treats a five-year-old resume like a current one; if you want recent candidates, add a date filter.
